import { DensitySmall, Power, PowerOff } from '@mui/icons-material'
import { Alert, AlertTitle, Box } from '@mui/material'
import { useQueries } from '@tanstack/react-query'
import Header from 'components/header'
import IconButton from 'components/icon-button'
import Loading from 'components/loading'
import Page from 'components/page'
import Wrapper from 'components/wrapper'
import { useAlert } from 'contexts/alert'
import { IOrganizationUserDTO } from 'data/dtos/authentication/i-organization-user-dto'
import { IAuthenticationsPermissions } from 'data/dtos/automation/i-authentications-permissions-dto'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'
import { loadItem, loadList } from 'services/requests'
import AuthenticationsHeaderInfo from '../header-info'

import RemovePermissionModal from '../remove-permission-modal'
import TableStatus from '../table-status'
import { useStyles } from './styles'

interface IRouteWithTableParams {
  id: string
}

const CofreDigitalTableInfo = () => {
  const localAlert = localStorage.getItem('alert-cofre') === 'true' ? false : true
  const [currentId, setCurrentId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [openPermissionModal, setOpenPermissionModal] = useState<boolean>(false)
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(localAlert)

  const classes = useStyles()
  const history = useHistory()
  const { openAlert } = useAlert()

  const { id } = useParams<IRouteWithTableParams>()

  const [permissionsData, organizationData] = useQueries({
    queries: [
      {
        queryKey: ['permissionsList'],
        queryFn: () =>
          loadList('/organizations-permissions/list', {
            search: '',
            page: 0,
            rowsPerPage: 100,
            columnOrder: [],
            organizationId: '',
          }),
        initialData: { data: { data: [] } } as any,
      },
      {
        queryKey: ['organization', id],
        queryFn: () => loadItem('/organizations', id),
        enabled: id != null,
      },
    ],
  })

  const loading = permissionsData.isLoading || organizationData.isLoading || isLoading

  const handleNavigationToTable = (outputId: string, id: string) => {
    history.push(`/cofre-digital/table/preview/${outputId}/${id}`)
  }

  const handleBackPage = () => history.push('/cofre-digital')

  const handleRefetch = async () => {
    await Promise.all([permissionsData.refetch(), organizationData.refetch()])
  }

  const handleOpenPermissionModal = (id: string) => {
    setCurrentId(id)
    setOpenPermissionModal((prev) => !prev)
  }

  const handleRemovePermission = async (id: string) => {
    setIsLoading(true)
    try {
      await api.delete(`/organizations-permissions/${id}`)
      openAlert('Permissão removida com sucesso', 'success')
      handleRefetch()
    } catch (error) {
      openAlert('Erro ao remover permissão', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreatePermission = useCallback(
    async (organizationIdRequest: string, keyPermission: string, isPermitted: boolean) => {
      setIsLoading(true)
      try {
        await api.post('organizations-permissions', {
          organizationIdRequest,
          keyPermission,
          isPermitted,
        })
        openAlert('Permissão criada com sucesso', 'success')
        handleRefetch()
      } catch (error) {
        openAlert('Erro ao criar permissão', 'error')
      } finally {
        setIsLoading(false)
      }
    },
    [handleRefetch]
  )

  const handleCloseAlert = () => {
    setIsAlertOpen(false)
    localStorage.setItem('alert-cofre', 'true')
  }

  if (loading) {
    return (
      <Wrapper>
        <Box className={classes.root}>
          <Loading />
        </Box>
      </Wrapper>
    )
  }

  const permissions: IAuthenticationsPermissions[] = permissionsData.data?.data.data
  const organization: IOrganizationUserDTO = organizationData.data?.data.data

  return (
    <Wrapper>
      <Header>
        <AuthenticationsHeaderInfo
          handleReturn={handleBackPage}
          organization={organization}
        />
      </Header>

      <Page>
        <Box className={classes.root}>
          {isAlertOpen && (
            <Alert
              className={classes.alert}
              severity='info'
              onClose={handleCloseAlert}
            >
              <AlertTitle>Visualize as permissões que seu fornecedor possui</AlertTitle>
              Você pode remover ou autorizar acesso de leitura da tabela ao seu fornecedor
            </Alert>
          )}

          <table className={classes.tableBase}>
            <thead className={classes.tableHeader}>
              <tr>
                <th className={classes.tableSize}>Nome</th>
                <th className={classes.tableSize}>Ultima Atualização</th>
                <th className={classes.tableSize}>Permissão de Leitura</th>
                <th className={classes.tableActionSize}></th>
              </tr>
            </thead>

            <tbody className={classes.tableBody}>
              {permissions.map((permission, index) => (
                <tr key={index}>
                  <td className={classes.tableSize}>
                    <span>{permission.name}</span>
                  </td>

                  <td className={classes.tableSize}>
                    {permission.lastUpdate && dayjs(permission.lastUpdate).format('DD/MM/YYYY - HH:mm:ss')}
                  </td>

                  <td className={classes.tableSize}>
                    <TableStatus status={permission.isPermitted ? 'authorized' : 'unauthorized'} />
                  </td>

                  <td className={classes.tableActionSize}>
                    <IconButton
                      icon={permission.isPermitted ? PowerOff : Power}
                      isWarning={permission.isPermitted}
                      isDisabled={false}
                      onClick={() => {
                        permission.isPermitted
                          ? handleOpenPermissionModal(permission.id)
                          : handleCreatePermission(
                              permission.organizationIdRequest,
                              permission.keyPermission,
                              !permission.isPermitted
                            )
                      }}
                      tooltip='Mudar Permissão'
                    />
                  </td>
                  <td className={classes.tableActionSize}>
                    <IconButton
                      icon={DensitySmall}
                      isDisabled={!permission.isPermitted}
                      onClick={() => {
                        handleNavigationToTable(permission.organizationIdOutput, permission.snowflakeTableId)
                      }}
                      tooltip='Ver Tabelas'
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Page>

      <RemovePermissionModal
        id={currentId}
        openModal={openPermissionModal}
        setOpenModal={handleOpenPermissionModal}
        removePermission={handleRemovePermission}
      />
    </Wrapper>
  )
}

export default CofreDigitalTableInfo
